import { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import { removeFromCart, fetchCart } from "../../store/slices/cart-slice";
import { useCartDrawer } from "../../contexts/CartDrawerContext";

const CartDrawer = () => {
  const { isOpen, closeDrawer } = useCartDrawer();
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const { cartItems, subtotal } = useSelector((state) => state.cart);

  // Lock page scroll while the drawer is open, and allow Escape to close it.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      const onKeyDown = (e) => {
        if (e.key === "Escape") closeDrawer();
      };
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("keydown", onKeyDown);
      };
    }
    return undefined;
  }, [isOpen, closeDrawer]);

  const handleRemoveFromCart = async (item) => {
    try {
      await dispatch(removeFromCart({ id: item.id, customizationId: item.customization?.customizationId })).unwrap();
      await dispatch(fetchCart());
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const formatPrice = (price) => {
    const convertedPrice = (price * currency.currencyRate).toFixed(2);
    return currency.currencySymbol + convertedPrice;
  };

  return (
    <Fragment>
      <div
        className={clsx("cart-drawer-backdrop", isOpen && "cart-drawer-backdrop--open")}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <aside
        className={clsx("cart-drawer", isOpen && "cart-drawer--open")}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        <div className="cart-drawer__header">
          <div>
            <h4>Your Cart</h4>
            <p className="cart-drawer__subtitle">
              {cartItems && cartItems.length > 0
                ? `${cartItems.length} item${cartItems.length > 1 ? "s" : ""} in your cart`
                : "Add items to get started"}
            </p>
          </div>
          <button
            className="cart-drawer__close"
            onClick={closeDrawer}
            aria-label="Close cart"
          >
            <i className="pe-7s-close" />
          </button>
        </div>

        <div className="cart-drawer__body">
          {cartItems && cartItems.length > 0 ? (
            <ul className="cart-drawer__items">
              {cartItems.map((item) => (
                <li className="cart-drawer__item" key={item.customization?.customizationId || item.id}>
                  <Link
                    to={process.env.PUBLIC_URL + "/product/" + item.slug}
                    onClick={closeDrawer}
                    className="cart-drawer__item-img"
                  >
                    <img
                      alt={item.name}
                      src={
                        (() => {
                          const img = item.images?.[0];
                          return typeof img === "string" ? img : img?.url;
                        })() || process.env.PUBLIC_URL + "/assets/img/product/placeholder.jpg"
                      }
                    />
                  </Link>
                  <div className="cart-drawer__item-info">
                    <Link
                      to={process.env.PUBLIC_URL + "/product/" + item.slug}
                      onClick={closeDrawer}
                    >
                      {item.name}
                    </Link>
                    <span className="cart-drawer__item-qty">Qty: {item.quantity}</span>
                    {item.customization && (
                      <span className="cart-drawer__item-custom-label">
                        Custom design
                      </span>
                    )}
                    <span className="cart-drawer__item-price">
                      {formatPrice(item.finalPrice)}
                      {item.quantity > 1 && (
                        <span className="cart-drawer__item-line-total">
                          {" "}
                          × {item.quantity} = {formatPrice(item.finalPrice * item.quantity)}
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    className="cart-drawer__item-remove"
                    onClick={() => handleRemoveFromCart(item)}
                    aria-label={`Remove ${item.name} from cart`}
                  >
                    <i className="fa fa-times-circle" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="cart-drawer__empty">
              <p>Your cart is empty</p>
              <Link
                to={process.env.PUBLIC_URL + "/shop"}
                onClick={closeDrawer}
                className="cart-drawer__btn cart-drawer__btn--primary btn-hover-fill"
              >
                Continue shopping
              </Link>
            </div>
          )}
        </div>

        {cartItems && cartItems.length > 0 && (
          <div className="cart-drawer__footer">
            <div className="cart-drawer__subtotal">
              <span>Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="cart-drawer__actions">
              <Link
                className="cart-drawer__btn cart-drawer__btn--primary btn-hover-fill"
                to={process.env.PUBLIC_URL + "/checkout"}
                onClick={closeDrawer}
              >
                Checkout
              </Link>
              <Link
                className="cart-drawer__btn cart-drawer__btn--secondary"
                to={process.env.PUBLIC_URL + "/cart"}
                onClick={closeDrawer}
              >
                View Cart
              </Link>
            </div>
          </div>
        )}
      </aside>
    </Fragment>
  );
};

export default CartDrawer;
